import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NumericTextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { FormsModule } from '@angular/forms';
import { MatDialogConfig } from '@angular/material/dialog';
import { DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { TextBoxModule } from '@syncfusion/ej2-angular-inputs';
import { SwitchModule } from '@syncfusion/ej2-angular-buttons';
import { HttpService } from '../../utility/http-service';
import { EditDialogService } from '../../utility/dialog/edit-dialog-service';
import { ClientInfo, ClientInfoViewModel } from '../../models/client-configuration.model';
import { RecentlyViewedService } from '../../services/recently-viewed.service';
import { CronScheduleEditorComponent } from '../../components/cron-schedule-editor/cron-schedule-editor.component';
import { ClientIconEditorComponent } from '../client-icon-editor/client-icon-editor.component';
import { ClientDataSourceComponent } from '../client-datasource-editor/client-data-source.component';
import { ClientDataFeedComponent } from '../client-data-feed/client-datafeed.component';
import { ClientSecurityExportsComponent } from '../client-security-exports/client-security-exports.component';
import { ClientDatasetEditorComponent } from '../client-dataset-editor/client-dataset-editor.component';
import { PartnerAssignmentComponent } from '../partner/partner-assignment.component';
import { RolesPanelComponent } from '../../components/roles-panel/roles-panel.component';
import { DataPolicyEditorComponent } from '../data-policy-editor/data-policy-editor.component';
import { ClientSyncEditorComponent } from '../client-sync-editor/client-sync-editor.component';

interface LocalUserInfo {
  isDeveloper: boolean;
  isGlobalAdmin: boolean;
  isCompanyAdmin: boolean;
}

@Component({
  selector: 'app-client-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule,
    DropDownListModule, NumericTextBoxModule, TextBoxModule, SwitchModule,
    CronScheduleEditorComponent, ClientDataSourceComponent,
    ClientDataFeedComponent, ClientSecurityExportsComponent,
    ClientDatasetEditorComponent, PartnerAssignmentComponent, RolesPanelComponent,
    DataPolicyEditorComponent, ClientSyncEditorComponent],
  templateUrl: './client-detail.component.html',
  styleUrls: ['./client-detail.component.css']
})

export class ClientDetailComponent implements OnInit {
  clientId!: string;
  client!: ClientInfoViewModel;
  possibleParents: any[] = [];
  dataModels: string[] = [];
  showKey = false;
  maskedKey = '********';
  loading = true;
  error: string | null = null;
  showWarning = false;
  originalKey?: string | null = null;
  editMode = false;
  userInfo?: LocalUserInfo;
  userInfoLoaded = false;
  tabsVisited = new Set<string>(['general']);
  clientLink: any = {};
  encryptionAlias: string | null = null;


  constructor(
    public dialogService: EditDialogService, private route: ActivatedRoute, private http: HttpService,
    private recentlyViewed: RecentlyViewedService,
  ) { }

  
  activateTab(tabName: string): void {
    this.tabsVisited.add(tabName);
  }

  ngOnInit(): void {
    this.loadUserInfo();
    this.clientId = this.route.snapshot.paramMap.get('id')!;

    if (!this.clientId) {
      this.error = "No client id in route.";
      this.loading = false;
      return;
    }

  
    const cached = localStorage.getItem(`client-${this.clientId}`);
    if (cached) {
      try {
        this.client = JSON.parse(cached);
        this.loading = false;
      } catch { }
    }

    this.http.get<ClientInfo>(
      `ClientSettings/GetClient?id=${this.clientId}`,
      (res: ClientInfo) => {
        this.client = res;
        this.recentlyViewed.add({
          label: res.displayName,
          url: `/client/${this.clientId}`,
          icon: 'building',
          type: 'client',
          colorKey: 'blue',
          subtitle: 'Client',
          timestamp: Date.now()
        });

        // Ensure reportsConfig is always fully initialized
        this.client.reportsConfig = {
          backfillReportMode: res.reportsConfig?.backfillReportMode ?? false,
          backfillReportLimit: res.reportsConfig?.backfillReportLimit,
          backfillTimeLimitHours: res.reportsConfig?.backfillTimeLimitHours,
          reportsEnabled: res.reportsConfig?.reportsEnabled ?? false,
          dataModel: res.reportsConfig?.dataModel ?? '',
          reportRecipients: res.reportsConfig?.reportRecipients ?? [],
          ssrsConfigurations: res.reportsConfig?.ssrsConfigurations ?? {}
        };

        this.loadPossibleParents();
        this.loadDataModels();
        this.loadEncryptionAlias()
        this.loadClientLink();
        this.loading = false;
      }
    );
  }

  get reportRecipientsText(): string {
    return (this.client?.reportsConfig?.reportRecipients ?? []).join('; ');
  }

  set reportRecipientsText(value: string) {
    if (this.client?.reportsConfig) {
      this.client.reportsConfig.reportRecipients = value
        .split(/[;,]/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    }
  }

  // A blank dataModel means "use WellChron" — so treat the two as the same value in the UI.
  get dataModelSelection(): string | null {
    return this.client?.reportsConfig?.dataModel || 'WellChron';
  }

  set dataModelSelection(value: string | null) {
    if (this.client?.reportsConfig) {
      this.client.reportsConfig.dataModel = (!value || value === 'WellChron') ? '' : value;
    }
  }

  loadDataModels(): void {
    this.http.get<string[]>('ClientSettings/GetDataModels', (res: string[]) => {
      this.dataModels = res ?? [];
    });
  }

  loadClientLink(): void {
    this.http.get<any>(
      `User/GetClientLink?clientId=${this.clientId}`,
      (res: any) => {
        this.clientLink = res ?? {};
      }
    );
  }

  loadEncryptionAlias(): void {
    if (!this.clientId) return;

    this.http.get<string | null>(
      `ClientSettings/GetEncryptionAlias?clientId=${this.clientId}`,
      (res: string | null) => {
        this.encryptionAlias = res ?? null;
      }
    );
  }

  private loadUserInfo(): void {
    this.http.get<LocalUserInfo>('User/GetUserInfo', (res: LocalUserInfo) => {
      this.userInfo = res;
      this.userInfoLoaded = true;
    }
    );
  }

  toggleEditMode() {
    if (!this.editMode) {
      this.originalKey = this.client?.encryptionKey;
      this.loadPossibleParents();
    } else {

      if (!this.client.parentClientId || this.client.parentClientId === '') {
        this.client.parentClientId = null;
      }

      const selectedParent = this.possibleParents.find(
        p => p.id === this.client.parentClientId
      );

      if (selectedParent) {
        this.client.parentCompanyName = selectedParent.displayName;
      }

      if (this.client.reportsConfig) {
        this.client.reportsPayload = JSON.stringify(this.client.reportsConfig);
      }
      const payload = {
        ...this.client,
        clientId: this.client.id,
        wdxMarketId: this.clientLink?.wdxMarketId ?? null,
        wdxUserId: this.clientLink?.wdxUserId ?? null,
        aliases: this.encryptionAlias != null
          ? [{ system: 2, alias: this.encryptionAlias }]
          : null,
        updateAlias: this.encryptionAlias != null,
      };
      this.http.post(
        'ClientSettings/UpdateClient',
        () => { this.editMode = false; },
        null,
        payload
      );
    }

    this.editMode = !this.editMode;
  }

  loadPossibleParents(): void {
    this.http.get("ClientSettings/GetPossibleClients", (res: any[]) => {
      this.possibleParents = res;

      if (this.client?.parentClientId) {
        const parent = this.possibleParents.find(
          p => p.id === this.client.parentClientId
        );

        if (parent) {
          this.client.parentCompanyName = parent.displayName;
        }
      }
    });
  }

  onEditIcon(client: any): void {
    if (!client) return;

    this.dialogService.openModal(ClientIconEditorComponent, (c: MatDialogConfig) => {
      c.data = client.id;
      c.minWidth = "fit-content";
    });
  }

  //toggleShowKey() {
  //  if (!this.showKey) {
  //    this.http.post(
  //      'ClientSettings/RevealEncryptionKey',
  //      (res: any) => {
  //        this.client.encryptionKey = res.value;
  //        this.showKey = true;

  //        setTimeout(() => {
  //          this.showKey = false;
  //          this.client.encryptionKey = null;
  //        }, 120000);
  //      },
  //      null,
  //      { id: this.client.id }
  //    );
  //  } else {
  //    this.showKey = false;
  //    this.client.encryptionKey = null;
  //  }
  //}

  confirmEncryptionChange() {
    const message =
      'Changing the encryption key may break existing client data. Continue?';

    if (!confirm(message)) {
      // revert to last known value
      this.client.encryptionKey = this.originalKey;
      return;
    }
  }

}

